import { useEffect, useRef, useState } from "react";
import { CheckCircleIcon, DocumentArrowDownIcon, PrinterIcon } from "@heroicons/react/24/outline";

import { useTillReceipt } from "../../data/useTill";
import useReceiptPrinter from "../../hooks/useReceiptPrinter";
import { ITillSale } from "../../models/till/ITillSale";
import PriceFormat from "../../utils/priceFormat";
import { ButtonInput } from "../shared/Inputs/ButtonInput";

interface SaleCompleteScreenProps {
  sale: ITillSale;
  operatorName?: string;
  onNewSale: () => void;
  onDownloadInvoice: () => Promise<void>;
}

const AUTO_PRINT_KEY = "till.autoPrint";

const readAutoPrint = (): boolean => {
  try {
    return localStorage.getItem(AUTO_PRINT_KEY) === "true";
  } catch {
    return false;
  }
};

/** Shown once the money is in: total, change, receipt and invoice actions, then on to the next customer. */
export default function SaleCompleteScreen({ sale, operatorName, onNewSale, onDownloadInvoice }: SaleCompleteScreenProps) {
  const receipt = useTillReceipt(sale.saleId);
  const { printReceipt } = useReceiptPrinter();
  const [autoPrint, setAutoPrint] = useState(readAutoPrint);
  const [downloading, setDownloading] = useState(false);
  const printedRef = useRef(false);

  useEffect(() => {
    try {
      localStorage.setItem(AUTO_PRINT_KEY, String(autoPrint));
    } catch {
      /* per-device convenience only */
    }
  }, [autoPrint]);

  useEffect(() => {
    if (autoPrint && receipt.data?.data && !printedRef.current) {
      printedRef.current = true;
      printReceipt(receipt.data.data, operatorName);
    }
    // printReceipt is recreated per render; the guard ref keeps this to one print.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPrint, receipt.data]);

  const download = async () => {
    setDownloading(true);
    try {
      await onDownloadInvoice();
    } finally {
      setDownloading(false);
    }
  };

  const cardText = sale.payment.method === "Card" && sale.payment.cardLast4
    ? `${(sale.payment.cardBrand ?? "card").toUpperCase()} •••• ${sale.payment.cardLast4}`
    : null;

  return (
    <div className="h-full flex items-center justify-center p-6">
      <div className="w-full max-w-xl rounded-2xl bg-white p-8 shadow-sm border border-gray-200 text-center">
        <CheckCircleIcon className="mx-auto h-16 w-16 text-emerald-600" />
        <h2 className="mt-2 text-2xl font-semibold text-gray-900">Sale complete</h2>
        <p className="text-sm text-gray-500">{sale.reference}{sale.invoiceNumberFormatted ? ` · ${sale.invoiceNumberFormatted}` : ""}</p>

        <p className="mt-6 text-5xl font-semibold tabular-nums text-gray-900">{PriceFormat(sale.total)}</p>
        <p className="mt-1 text-gray-600">
          Paid by {sale.payment.method === "BankTransfer" ? "bank transfer" : sale.payment.method.toLowerCase()}{cardText ? ` (${cardText})` : ""}
        </p>

        {sale.payment.method === "Cash" && sale.changeDue != null ? (
          <div className="mt-6 rounded-xl bg-emerald-50 p-4">
            <p className="text-sm uppercase tracking-wide text-emerald-700">Change due</p>
            <p className="text-4xl font-semibold tabular-nums text-emerald-800">{PriceFormat(sale.changeDue)}</p>
            {sale.cashTendered != null ? <p className="text-sm text-emerald-700">Tendered {PriceFormat(sale.cashTendered)}</p> : null}
          </div>
        ) : null}

        <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ButtonInput
            label=""
            size="lg"
            classes="!bg-white !text-gray-800 border !border-gray-300 hover:!bg-gray-50 h-14 !text-base"
            onClick={() => receipt.data?.data && printReceipt(receipt.data.data, operatorName)}
            disabled={!receipt.data?.data}
            isLoading={receipt.isLoading}
          >
            <span className="inline-flex items-center gap-2"><PrinterIcon className="h-5 w-5" /> Print receipt</span>
          </ButtonInput>
          <ButtonInput
            label=""
            size="lg"
            classes="!bg-white !text-gray-800 border !border-gray-300 hover:!bg-gray-50 h-14 !text-base"
            onClick={download}
            disabled={!sale.invoiceId}
            isLoading={downloading}
          >
            <span className="inline-flex items-center gap-2"><DocumentArrowDownIcon className="h-5 w-5" /> Invoice PDF</span>
          </ButtonInput>
        </div>

        <label className="mt-4 inline-flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" className="h-5 w-5 rounded border-gray-300 text-primary-600" checked={autoPrint} onChange={(e) => setAutoPrint(e.target.checked)} />
          Print a receipt automatically after each sale
        </label>

        <div className="mt-8">
          <ButtonInput label="New sale" size="lg" classes="w-full h-16 !text-lg" onClick={onNewSale} />
        </div>
      </div>
    </div>
  );
}
